import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Truck, CheckCircle2, ChevronRight, ClipboardList, FileText,
} from "lucide-react";
import AuditTrail from "@/components/AuditTrail";

const STATUS_COLORS = {
  "Received": "bg-slate-100 text-slate-700 border-slate-200",
  "Under Analysis": "bg-amber-50 text-amber-800 border-amber-200",
  "Quotation Pending": "bg-blue-50 text-blue-800 border-blue-200",
  "Quotation Sent": "bg-indigo-50 text-indigo-800 border-indigo-200",
  "PO Received": "bg-teal-50 text-teal-800 border-teal-200",
  "OA Created": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Closed": "bg-slate-100 text-slate-500 border-slate-200",
};

export default function ReceivingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusRemark, setStatusRemark] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get(`/receiving/${id}`);
      setD(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !d) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;

  const cls = STATUS_COLORS[d.status] || "bg-slate-100 text-slate-700 border-slate-200";
  const locked = d.status === "OA Created" || d.status === "Closed";

  async function toggleAnalysis() {
    setSaving(true);
    try {
      const { data } = await api.patch(`/receiving/${id}/analysis`, { analysis_report_done: !d.analysis_report_done });
      setD(data);
      toast.success(data.analysis_report_done ? "Analysis marked done" : "Analysis reopened");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  async function changeStatus() {
    if (!newStatus) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/receiving/${id}/status`, { status: newStatus, remarks: statusRemark || null });
      setD(data);
      setStatusDialog(false);
      setNewStatus(""); setStatusRemark("");
      toast.success(`Status → ${data.status}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="receiving-detail-page" className="space-y-5 pb-8">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <button onClick={() => nav("/receiving")} className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Receiving
        </button>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-700">{d.dc_number}</span>
      </div>

      {/* Header */}
      <Card className="border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold flex items-center gap-1.5">
              <Truck className="w-3 h-3" /> Delivery Challan · Reconditioning
            </div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900" data-testid="rec-dc-number-header">
              {d.dc_number}
            </div>
            <div className="mt-2 text-sm text-slate-600">
              {d.customer_name}{d.plant_name ? ` · ${d.plant_name}` : ""} · {d.dc_date}
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-2">
            <span data-testid="rec-status-chip"
                  className={`inline-flex items-center border px-2 py-0.5 rounded text-[11px] font-bold uppercase font-mono ${cls}`}>
              {d.status}
            </span>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant={d.analysis_report_done ? "default" : "outline"}
                      disabled={locked || saving} onClick={toggleAnalysis}
                      data-testid="rec-toggle-analysis"
                      className={d.analysis_report_done ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                {d.analysis_report_done ? "Analysis Done" : "Mark Analysis Done"}
              </Button>
              <Button size="sm" variant="outline" disabled={locked}
                      data-testid="rec-change-status" onClick={() => { setNewStatus(d.status); setStatusDialog(true); }}>
                Change Status
              </Button>
              {d.status === "Quotation Pending" && (
                <Button size="sm" data-testid="rec-create-quotation"
                        onClick={() => nav(`/quotations/new?dc_id=${d.id}`)}
                        className="bg-red-700 hover:bg-red-800 text-white">
                  <FileText className="w-3.5 h-3.5 mr-1" /> Create Quotation
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Details grid */}
      <Card className="border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>
          Receiving Details
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 text-sm">
          {[
            ["Department", d.department],
            ["Contact Person", d.contact_person],
            ["Contact Phone", d.contact_phone],
            ["Transporter", d.transporter],
            ["Vehicle Number", d.vehicle_number],
            ["Cylinders Received", d.num_cylinders_received, "font-mono font-bold text-lg text-blue-900"],
            ["Analysis Completed", d.analysis_completed_date, "font-mono"],
            ["Received On", d.created_at?.slice(0, 10), "font-mono"],
          ].map(([label, value, extra]) => (
            <div key={label}>
              <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
              <dd className={`mt-0.5 text-slate-800 ${extra || ""}`}>{value != null && value !== "" ? value : <span className="text-slate-300">—</span>}</dd>
            </div>
          ))}
        </dl>
        {d.remarks && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Remarks</div>
            <div className="mt-1 text-sm text-slate-700 italic">"{d.remarks}"</div>
          </div>
        )}
      </Card>

      {/* Linked commercial documents */}
      <Card className="border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Linked Documents</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            ["Quotation", d.quotation, d.quotation_id ? `/quotations/${d.quotation_id}` : null, (q) => q?.display_number || q?.quotation_number],
            ["Purchase Order", d.purchase_order, null, (p) => p?.po_number],
            ["Order Acceptance", d.order_acceptance, null, (o) => o?.oa_number],
            ["Cylinders", d.cylinders, null, () => `${(d.cylinders || []).length} generated`],
          ].map(([label, val, href, fmt]) => {
            const shown = val && (Array.isArray(val) ? val.length > 0 : true);
            const body = (
              <div className={`border rounded p-3 ${shown ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-slate-50/40"}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                <div className={`mt-1 font-mono text-xs ${shown ? "text-slate-800 font-bold" : "text-slate-400"}`}>
                  {shown ? fmt(val) : "— not yet"}
                </div>
              </div>
            );
            return href ? <Link key={label} to={href} className="hover:opacity-80">{body}</Link> : <div key={label}>{body}</div>;
          })}
        </div>
        {(d.cylinders || []).length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Generated Cylinders
            </div>
            <div className="flex flex-wrap gap-2" data-testid="rec-cylinder-chips">
              {(d.cylinders || []).map((c) => (
                <Link key={c.id} to={`/cylinders/${c.id}`}
                      className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50/60 hover:bg-blue-100 rounded px-2 py-1 text-xs">
                  <span className="font-mono font-bold text-blue-900">#{c.cylinder_number}</span>
                  <span className="text-slate-500">{c.current_stage}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Audit trail */}
      <Card className="border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardList className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Audit Trail</div>
        </div>
        <AuditTrail items={d.audit_trail || []} />
      </Card>

      {/* Status change dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent data-testid="rec-status-dialog">
          <DialogHeader>
            <DialogTitle>Change Delivery Challan Status</DialogTitle>
            <DialogDescription>
              Move this DC through its lifecycle. Analysis toggle and quotation creation update this automatically — use this only for manual overrides.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="rec-new-status"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {Object.keys(STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Remarks</Label>
              <Textarea data-testid="rec-status-remark" rows={2} value={statusRemark}
                        onChange={(e) => setStatusRemark(e.target.value)} placeholder="Why?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>Cancel</Button>
            <Button data-testid="rec-status-confirm" disabled={saving || !newStatus} onClick={changeStatus}
                    className="bg-red-700 hover:bg-red-800 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
